import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePagePermissions } from '../hooks/usePagePermissions';
import SupportChat from './support/SupportChat';
import {
  HomeIcon,
  DocumentPlusIcon,
  DocumentTextIcon,
  UserIcon,
  CogIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  TableCellsIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  BookOpenIcon,
  QueueListIcon,
  CreditCardIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const isEmbedded = searchParams.get('embedded') === 'true';
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { canViewPage } = usePagePermissions();

  // Regular user navigation with page name mapping
  const userNavigation = [
    { name: 'Dashboard', href: '/app/dashboard', icon: HomeIcon, pageName: 'dashboard' },
    { name: 'Canvas Invoice Form', href: '/app/invoice/canvas', icon: DocumentPlusIcon, pageName: 'canvas_builder' },
    { name: 'Search Invoice', href: '/app/invoice/compact', icon: TableCellsIcon, pageName: 'invoice_new' },
    { name: 'Invoice Generator', href: '/app/scenarios', icon: BookOpenIcon, pageName: 'scenarios' },
    { name: 'My Invoices', href: '/app/invoices', icon: DocumentTextIcon, pageName: 'invoice_list' },
    { name: 'Batch Invoices', href: '/app/batch-invoices', icon: QueueListIcon, pageName: 'invoice_batch', disabled: true },
    { name: 'FBR API Checker', href: '/app/fbr-api-checker', icon: ShieldCheckIcon, pageName: 'fbr_checker' },
    { name: 'SRO Chat', href: '/app/sro-chat', icon: DocumentTextIcon, pageName: 'sro_chat' },
    { name: 'Reports', href: '/app/reports', icon: ChartBarIcon, pageName: 'reports' },
    { name: 'Reference Data', href: '/app/reference-data', icon: CogIcon, pageName: 'reference_data' },
    { name: 'Subscription', href: '/app/subscription', icon: CreditCardIcon, pageName: 'subscription' },
    { name: 'Subscription History', href: '/app/subscription-history', icon: CreditCardIcon, pageName: 'subscription' },
    { name: 'Blog', href: '/blog', icon: PencilIcon, pageName: 'dashboard' },
    { name: 'Support Chat', href: '/app/support', icon: ChatBubbleLeftRightIcon, pageName: 'support' },
    { name: 'Profile', href: '/app/profile', icon: UserIcon, pageName: 'profile' },
  ];

  // Admin-specific navigation (simplified - only main admin panel link)
  const adminNavigation = [
    { name: 'Admin Panel', href: '/app/admin', icon: ShieldCheckIcon, isAdmin: true, pageName: 'admin_dashboard' },
  ];

  // Filter navigation based on permissions
  const getFilteredNavigation = () => {
    let filtered = userNavigation.filter(item => 
      item.pageName ? canViewPage(item.pageName) : true
    );

    if (user?.role === 'admin') {
      filtered = [
        ...filtered,
        { type: 'divider' },
        { type: 'header', name: 'Administration' },
        ...adminNavigation
      ];
    }

    return filtered;
  };

  const navigation = getFilteredNavigation();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isCurrentPage = (href) => {
    return location.pathname === href;
  };

  if (isEmbedded) {
    return (
      <div className="min-h-screen bg-gray-50">
        <main className="flex-1">
          <div className="py-6">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <Outlet />
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-40 lg:hidden ${sidebarOpen ? '' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-white shadow-xl">
          <div className="flex h-16 items-center justify-between px-4">
            <h1 className="text-xl font-bold text-gray-900">PRAL DI System</h1>
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          
          <nav className="flex-1 space-y-1 px-2 py-4">
            {navigation.map((item, index) => {
              // Skip disabled items
              if (item.disabled) {
                return null;
              }

              // Handle divider
              if (item.type === 'divider') {
                return (
                  <div key={`divider-${index}`} className="border-t border-gray-200 my-4"></div>
                );
              }

              // Handle header
              if (item.type === 'header') {
                return (
                  <div key={item.name} className="px-2 py-2">
                    <h3 className="text-xs font-semibold text-red-600 uppercase tracking-wider flex items-center">
                      <ShieldCheckIcon className="h-4 w-4 mr-1" />
                      {item.name}
                    </h3>
                  </div>
                );
              }

              // Handle regular navigation item
              const Icon = item.icon;
              const isAdminItem = item.isAdmin;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                    isCurrentPage(item.href)
                      ? isAdminItem
                        ? 'bg-red-100 text-red-900 border-l-4 border-red-500'
                        : 'bg-blue-100 text-blue-900'
                      : isAdminItem
                        ? 'text-red-600 hover:bg-red-50 hover:text-red-900'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <Icon className={`mr-3 h-6 w-6 flex-shrink-0 ${isAdminItem ? 'text-red-500' : ''}`} />
                  {item.name}
                </Link>
              );
            })}
          </nav>
          
          <div className="border-t border-gray-200 p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center">
                  <span className="text-sm font-medium text-white">
                    {user?.business_name?.charAt(0) || 'U'}
                  </span>
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-700">{user?.business_name}</p>
                <p className="text-xs text-gray-500">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="mt-3 w-full flex items-center px-2 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md"
            >
              <ArrowRightOnRectangleIcon className="mr-3 h-5 w-5" />
              Sign out
            </button>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow bg-white border-r border-gray-200">
          <div className="flex h-16 items-center px-4 border-b border-gray-200">
            <Link to="/home" className="flex items-center space-x-2">
              <img src="/fbrlogo1.svg" alt="Logo" className="h-8 w-8" />
              <span className="text-xl font-bold text-gray-900">PRAL DI System</span>
            </Link>
          </div>
          
          <nav className="flex-1 space-y-1 px-2 py-4">
            {navigation.map((item, index) => {
              // Skip disabled items
              if (item.disabled) {
                return null;
              }

              // Handle divider
              if (item.type === 'divider') {
                return (
                  <div key={`divider-${index}`} className="border-t border-gray-200 my-4"></div>
                );
              }

              // Handle header
              if (item.type === 'header') {
                return (
                  <div key={item.name} className="px-2 py-2">
                    <h3 className="text-xs font-semibold text-red-600 uppercase tracking-wider flex items-center">
                      <ShieldCheckIcon className="h-4 w-4 mr-1" />
                      {item.name}
                    </h3>
                  </div>
                );
              }

              // Handle regular navigation item
              const Icon = item.icon;
              const isAdminItem = item.isAdmin;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                    isCurrentPage(item.href)
                      ? isAdminItem
                        ? 'bg-red-100 text-red-900 border-l-4 border-red-500'
                        : 'bg-blue-100 text-blue-900'
                      : isAdminItem
                        ? 'text-red-600 hover:bg-red-50 hover:text-red-900'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <Icon className={`mr-3 h-6 w-6 flex-shrink-0 ${isAdminItem ? 'text-red-500' : ''}`} />
                  {item.name}
                </Link>
              );
            })}
          </nav>
          
          <div className="border-t border-gray-200 p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center">
                  <span className="text-sm font-medium text-white">
                    {user?.business_name?.charAt(0) || 'U'}
                  </span>
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-700">{user?.business_name}</p>
                <p className="text-xs text-gray-500">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="mt-3 w-full flex items-center px-2 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md"
            >
              <ArrowRightOnRectangleIcon className="mr-3 h-5 w-5" />
              Sign out
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <div className="sticky top-0 z-10 bg-white border-b border-gray-200 lg:hidden">
          <div className="flex h-16 items-center justify-between px-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="text-gray-400 hover:text-gray-600"
            >
              <Bars3Icon className="h-6 w-6" />
            </button>
            <Link to="/home" className="flex items-center space-x-2">
              <img src="/fbrlogo1.svg" alt="Logo" className="h-8 w-8" />
              <span className="text-lg font-semibold text-gray-900">PRAL DI System</span>
            </Link>
            <div className="w-6" /> {/* Spacer */}
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1">
          <div className="py-6">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <Outlet />
            </div>
          </div>
        </main>
      </div>

      {/* Floating Support Chat - only show on non-support pages */}
      {!location.pathname.includes('/app/support') && <SupportChat />}
    </div>
  );
};

export default Layout;
